import { useState } from "react";
import Modal from "../components/Modal";
import {
  downgradeSubscription,
  Plan,
  CurrentSubscription,
} from "../api/subscription";
import { formatCents, formatNumber, formatDate } from "../utils/format";
import "./planSwitch.scss";

const feeLabel = (cents: number) =>
  cents <= 0
    ? "$0"
    : `$${(cents / 100).toLocaleString("en-US", { maximumFractionDigits: 2 })}`;

const DowngradeForm: React.FC<{
  currentPlan?: Plan;
  targetPlan: Plan;
  currentSub: CurrentSubscription | null;
  onSuccess: () => void;
  onClose: () => void;
}> = ({ currentPlan, targetPlan, currentSub, onSuccess, onClose }) => {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [doneMsg, setDoneMsg] = useState<string | null>(null);

  const effectiveDate = currentSub?.periodEnd
    ? formatDate(currentSub.periodEnd)
    : "the current cycle end";

  const handleDowngrade = async () => {
    setError(null);
    setSubmitting(true);
    try {
      const res = await downgradeSubscription();
      setDoneMsg(res.message);
      onSuccess();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to downgrade.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="cs-plan-switch">
      <div className="cs-plan-switch__header">
        <h2 className="cs-plan-switch__title">
          Switch to {targetPlan.planName}
        </h2>
        <button
          type="button"
          className="cs-plan-switch__close"
          onClick={onClose}
        >
          ✕
        </button>
      </div>
      <p className="cs-plan-switch__subtitle">
        You are switching from {currentPlan?.planName || "current plan"} (
        {feeLabel(currentPlan?.monthlyFeeCents ?? 0)}/mo) to{" "}
        {targetPlan.planName} ({feeLabel(targetPlan.monthlyFeeCents)}/mo).
      </p>

      <div className="cs-plan-switch__box">
        <div className="cs-plan-switch__row">
          <span className="cs-plan-switch__muted">Effective Date: </span>
          Current cycle ends ({effectiveDate})
        </div>
        <div className="cs-plan-switch__row">
          <span className="cs-plan-switch__muted">Current Plan: </span>
          {currentPlan?.planName || "Current plan"} remains active until the
          effective date
        </div>
        <div className="cs-plan-switch__row">
          <span className="cs-plan-switch__muted">Monthly Credits: </span>
          {formatCents(currentPlan?.monthlyCreditsCents ?? 0)}
          <span className="cs-plan-switch__arrow">→</span>
          {formatCents(targetPlan.monthlyCreditsCents)} after effective date
        </div>
        <div className="cs-plan-switch__row">
          <span className="cs-plan-switch__muted">Rate Limit: </span>
          {formatNumber(currentPlan?.rateLimitQps ?? 0)}
          <span className="cs-plan-switch__arrow">→</span>
          {formatNumber(targetPlan.rateLimitQps)} req/s
        </div>
      </div>

      <div className="cs-plan-switch__warn">
        No refund for the current billing cycle
        <br />
        Purchased credits ({formatCents(
          currentSub?.purchasedCreditBalance ?? 0
        )}) are yours permanently
      </div>

      {error && <p className="cs-plan-switch__error">{error}</p>}
      {doneMsg && <p className="cs-plan-switch__success">✓ {doneMsg}</p>}

      <div className="cs-plan-switch__actions">
        <button
          type="button"
          className="cs-plan-switch__cancel"
          onClick={onClose}
        >
          {doneMsg ? "Close" : "Cancel"}
        </button>
        {!doneMsg && (
          <button
            type="button"
            className="cs-plan-switch__primary"
            disabled={submitting}
            onClick={handleDowngrade}
          >
            {submitting ? "Processing…" : "Confirm — Effective at End of Cycle"}
          </button>
        )}
      </div>
    </div>
  );
};

/** 降级套餐弹窗（流程四） */
const DowngradeModal: React.FC<{
  open: boolean;
  onClose: () => void;
  currentPlan?: Plan;
  targetPlan: Plan | null;
  currentSub: CurrentSubscription | null;
  onSuccess: () => void;
}> = ({ open, onClose, currentPlan, targetPlan, currentSub, onSuccess }) => {
  if (!targetPlan) return null;
  return (
    <Modal open={open} onClose={onClose}>
      <DowngradeForm
        currentPlan={currentPlan}
        targetPlan={targetPlan}
        currentSub={currentSub}
        onSuccess={onSuccess}
        onClose={onClose}
      />
    </Modal>
  );
};

export default DowngradeModal;
